import React, { useEffect, useRef } from 'react';
import { useTemp, useErrorSnackbar } from 'swipeclock-frontend';
import { Redirect } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PropTypes from 'prop-types';
import { useTheme, styled } from '@mui/material/styles';
import { useCountdown } from '../hooks/useTimer';
import { useTimeStamp } from '../hooks/useTimer';
import { useSuccessfulPunchCompletion } from '../hooks/useSuccessfulPunchCompletion';
import PoweredByFooter from './shared/PoweredByFooter';
import { useWhiteLabelContext } from 'providers/WhiteLabelProvider';

// Shared components
import PromptContentCard from './shared/PromptContentCard';
import ActionButton from './shared/ActionButton';

import {
  HeaderStack,
  CountdownProgress,
  TimerText,
  TitleText,
  MessageText,
} from './shared/PromptStyled';

export default function PromptSuccess() {
  const errorRef = useRef(false);
  const theme = useTheme();
  const { state: whiteLabelState } = useWhiteLabelContext();
  const { finish, shouldSignOut } = useSuccessfulPunchCompletion();

  // Completes the punch after 20 seconds.
  // Ensures the timer is running, assuming it was paused by the ButtonPad punch request
  const { timeLeft, progressPercentage } = useCountdown(20, finish);

  return (
    <>
      {errorRef.current ? (
        <ErrorRedirect />
      ) : (
        <>
          <PromptContentCard
            accentColor={{
              main: theme.palette.success.main,
              light: theme.palette.success.light,
            }}
          >
            <Prompt
              errorRef={errorRef}
              finish={finish}
              shouldSignOut={shouldSignOut}
            />
            <CountdownProgress
              variant="determinate"
              value={progressPercentage}
            />
            <TimerText variant="caption">{timeLeft}s</TimerText>
          </PromptContentCard>
          <PoweredByFooter whiteLabelState={whiteLabelState} />
        </>
      )}
    </>
  );
}

// #########################
// ### Child Components ###
// #########################

// Using React.memo to prevent unnecessary re-renders
const Prompt = React.memo(function Prompt({ errorRef, finish, shouldSignOut }) {
  const { temp: cache } = useTemp('webClock');
  const timeStamp = useTimeStamp(true);

  const { screenName, employeeName } = cache || {};
  let title = '';
  let message = '';
  let btnMessage = '';

  // Each get will throw an error if the screenName is not recognized
  // Catch updates a ref to trigger the error redirect in one render
  try {
    title = getPunchActionTitle(screenName);
    message = getMessage(screenName, employeeName, timeStamp);
    btnMessage = getBtnMessage(screenName);
  } catch (e) {
    if (!errorRef.current) {
      errorRef.current = true;
      console.log(`%c${e}`, 'color: red');
      console.error(e);
    }
  }

  return (
    <>
      <HeaderStack>
        <SuccessIcon fontSize="large" />
        <TitleText variant="h4">{title}</TitleText>
      </HeaderStack>
      <MessageText variant="h5">{message}</MessageText>
      {shouldSignOut ? (
        <ActionButton id="sc-success_return_to_clock_button" onClick={finish}>
          {btnMessage}
        </ActionButton>
      ) : (
        <ActionButton id="sc-success_return_to_clock_button" to="/clock/web">
          {btnMessage}
        </ActionButton>
      )}
    </>
  );
});

Prompt.propTypes = {
  errorRef: PropTypes.any,
  finish: PropTypes.func,
  shouldSignOut: PropTypes.bool,
};

function ErrorRedirect() {
  const redirectedRef = useRef(false);
  const setErrorSnackbar = useErrorSnackbar();

  useEffect(() => {
    if (!redirectedRef.current) {
      redirectedRef.current = true;
      setErrorSnackbar(
        'An error occurred while processing the punch action. Please try again.'
      );
    }
  }, [setErrorSnackbar]);

  return <Redirect to="/clock/web" />;
}

// #########################
// ### Styled Components ###
// #########################

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: '2.5rem',
  color: theme.palette.success.main,
}));

// #########################
// ### Helper Functions ###
// #########################

// utils/promptMessages.js
function getPunchActionTitle(screenName) {
  switch (screenName) {
    case 'EARLYIN_SUCCESS':
    case 'ONTIMEIN_SUCCESS':
    case 'LATEIN_SUCCESS':
      return 'Clocked In';
    case 'EARLYOUT_SUCCESS':
    case 'ONTIMEOUT_SUCCESS':
    case 'LATEOUT_SUCCESS':
      return 'Clocked Out';
    case 'OUTBREAK_SUCCESS':
      return 'Break started';
    case 'INBREAK_SUCCESS':
      return 'Break ended';
    case 'OUTMEAL_SUCCESS':
      return 'Meal started';
    case 'INMEAL_SUCCESS':
      return 'Meal ended';
    case 'TRANSFER':
      return 'Transfer completed';
    default:
      throw new Error(`Unknown screen name: ${screenName}`);
  }
}

function getMessage(screenName, employeeName, timeStamp) {
  switch (screenName) {
    case 'EARLYIN_SUCCESS':
      return `Hi ${employeeName}, you clocked in early at ${timeStamp}`;
    case 'ONTIMEIN_SUCCESS':
      return `Hi ${employeeName}, you clocked in at ${timeStamp}`;
    case 'LATEIN_SUCCESS':
      return `Hi ${employeeName}, you were clocked in late at ${timeStamp}`;
    case 'EARLYOUT_SUCCESS':
      return `Hi ${employeeName}, you clocked out early at ${timeStamp}`;
    case 'ONTIMEOUT_SUCCESS':
      return `Hi ${employeeName}, you clocked out at ${timeStamp}`;
    case 'LATEOUT_SUCCESS':
      return `Hi ${employeeName}, you were clocked out late at ${timeStamp}`;
    case 'OUTBREAK_SUCCESS':
      return `${employeeName}, your break started at ${timeStamp}`;
    case 'INBREAK_SUCCESS':
      return `${employeeName}, your break ended at ${timeStamp}`;
    case 'OUTMEAL_SUCCESS':
      return `${employeeName}, your meal started at ${timeStamp}`;
    case 'INMEAL_SUCCESS':
      return `${employeeName}, your meal ended at ${timeStamp}`;
    case 'TRANSFER':
      return `Hi ${employeeName}, you transferred at ${timeStamp}`;
    default:
      throw new Error(`Unknown screen name: ${screenName}`);
  }
}

function getBtnMessage(screenName) {
  switch (screenName) {
    case 'LATEIN_SUCCESS':
      return 'FINISH CLOCK IN';
    case 'LATEOUT_SUCCESS':
      return 'FINISH CLOCK OUT';
    case 'OUTBREAK_SUCCESS':
    case 'OUTMEAL_SUCCESS':
      return 'OFF TO RELAX';
    case 'ONTIMEIN_SUCCESS':
    case 'ONTIMEOUT_SUCCESS':
    case 'EARLYIN_SUCCESS':
    case 'EARLYOUT_SUCCESS':
    case 'INBREAK_SUCCESS':
    case 'INMEAL_SUCCESS':
    case 'TRANSFER':
      return 'GOT IT';
    default:
      throw new Error(`Unknown screen name: ${screenName}`);
  }
}
